import { Button } from "@mui/material"
import { useFormikContext } from "formik"
import { Upload } from "lucide-mui"
import React, { useState } from "react"

import { GetVenueFullQuery } from "@/graphql/graphql"
import i18n from "@/i18n"

interface VenueLogoProps {
  venue: GetVenueFullQuery["venue"]
}

export interface VenueLogoFormValues {
  venueLogo: File | null
}

const AVATAR_SIZE = "w-[105px] h-[105px]"

const STYLES = {
  container: "bg-white rounded-lg p-6",
  header: "text-xl font-semibold mb-4 mt-0",
  contentWrapper: "flex gap-6 items-start",
  imageWrapper: "flex-shrink-0",
  image: `${AVATAR_SIZE} object-cover rounded-lg border border-gray-300`,
  placeholder: `${AVATAR_SIZE} bg-gray-100 rounded-lg border border-gray-300 flex items-center justify-center`,
  placeholderText: "text-gray-400 text-sm",
  textWrapper: "flex flex-col gap-4",
  title: "font-bold text-lg mb-1",
  description: "text-gray-600 text-sm",
} as const

const VenueLogo: React.FC<VenueLogoProps> = ({ venue }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const formik = useFormikContext<VenueLogoFormValues>()

  const venueLogoImage = venue?.images?.find((img) => img.kind === "venue_logo")
  const displayUrl = previewUrl || venueLogoImage?.url

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const url = URL.createObjectURL(file)
      setPreviewUrl(url)
      void formik.setFieldValue("venueLogo", file)
    }
  }

  return (
    <div className={STYLES.container} data-testid="settings-venue-logo-container">
      <h2 className={STYLES.header}>{i18n.t("app.settings.venues.tabbio.venue_logo.header")}</h2>
      <div className={STYLES.contentWrapper}>
        <div className={STYLES.imageWrapper}>
          {displayUrl ? (
            <img src={displayUrl} alt="Venue logo" className={STYLES.image} data-testid="settings-venue-logo-image" />
          ) : (
            <div className={STYLES.placeholder}>
              <span className={STYLES.placeholderText}>No logo</span>
            </div>
          )}
        </div>

        <div className={STYLES.textWrapper}>
          <div>
            <div className={STYLES.title}>Venue logo</div>
            <div className={STYLES.description}>Upload a square image, at least 200x200px.</div>
          </div>

          <div>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: "none" }}
              id="venue-logo-upload"
              data-testid="settings-venue-logo-input"
            />
            <label htmlFor="venue-logo-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<Upload />}
                data-testid="settings-venue-logo-btn-upload"
              >
                Upload New
              </Button>
            </label>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VenueLogo
